import { useState, memo } from 'react';
import { Stack, Image, Text, Flex, Button } from '@chakra-ui/react';
import { Robot } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

import emptyInterviewImage from '@/assets/image/empty-interview-cover.svg';
import { AgencyPlan, AgencyUsageType } from '@/types/enums';
import { Job } from '@/types/Job';
import { CandidateWithJobContext } from '@/types/Candidate';
import AddInterviewDialog from '@/views/Interview/Copilot/AddInterviewDialog';
import { LimitExhaustedDialog } from '@/views/Interview/Agent/LimitExhaustedDialog';
import useAuth from '@/store/auth';
import { fetchAPI } from '@/api/fetch';
import { apiGetAccountProfile } from '@/api/services/account.service';

interface InterviewEmptyStateProps {
  onAddInterview: () => void;
  job: Job;
  candidate: CandidateWithJobContext;
}

const InterviewEmptyState = memo(({ onAddInterview, job, candidate }: InterviewEmptyStateProps) => {
  const navigate = useNavigate();
  const { account } = useAuth();
  const [isLimitDialogOpen, setIsLimitDialogOpen] = useState(false);
  const [isFreePlan, setIsFreePlan] = useState(false);

  // For copilot meeting time usage
  const meetingTimeUsage = account?.agency?.usages?.find(
    (usage) => usage.type === AgencyUsageType.MeetingTime
  );
  const isExhausted = meetingTimeUsage?.exhausted || false;
  const plan = account?.agency?.plan || AgencyPlan.FREE;

  const handleCreateScreening = async () => {
    // Fetch fresh account data to check latest limits
    try {
      const response = await fetchAPI<Account>({
        request: () => apiGetAccountProfile()
      });

      if (response.success && response.data) {
        const freshAccount = response.data;
        const agenticInterviewsUsage = freshAccount.agency?.usages?.find(
          (usage) => usage.type === AgencyUsageType.AgenticInterviews
        );
        const isExhausted = agenticInterviewsUsage?.exhausted || false;
        const isPlanFree = freshAccount.agency?.plan === AgencyPlan.FREE;

        if (isExhausted || isPlanFree) {
          setIsFreePlan(isPlanFree);
          setIsLimitDialogOpen(true);
        } else {
          navigate('/interview/agent/create-session', {
            state: {
              prefillData: {
                client: job.client,
                job: job,
                candidate: candidate
              }
            }
          });
        }
      } else {
        // If API call fails, allow navigation
        navigate('/interview/agent/create-session', {
          state: {
            prefillData: {
              client: job.client,
              job: job,
              candidate: candidate
            }
          }
        });
      }
    } catch {
      // If error occurs, allow navigation
      navigate('/interview/agent/create-session', {
        state: {
          prefillData: {
            client: job.client,
            job: job,
            candidate: candidate
          }
        }
      });
    }
  };

  return (
    <Stack align="center" mt="64px" mb="32px">
      <Image w="210px" h="210px" src={emptyInterviewImage} alt="Empty Interview" mb="60px" />
      <Text fontSize="24px" fontWeight="bold" color="neutrals.800" mb="8px">
        No interview is scheduled yet
      </Text>
      <Text fontSize="18px" color="neutrals.500" mb="24px">
        Once scheduled, all interview details will appear here.
      </Text>
      <Flex gap="16px" flexWrap="wrap" justify="center">
        <Button
          color="primary.900"
          border="1px solid"
          borderColor="primary.900"
          bg="neutrals.0"
          borderRadius="8px"
          fontWeight="semibold"
          fontSize="14px"
          p="10px 24px"
          _hover={{ bg: 'primary.900', color: 'neutrals.0' }}
          aria-label="Add a new session"
          onClick={handleCreateScreening}
        >
          Create automated screening
          <Robot size={24} aria-hidden="true" />
        </Button>
        <AddInterviewDialog
          mode="add-candidate"
          onAddInterview={onAddInterview}
          job={job}
          candidate={candidate}
          isExhausted={isExhausted}
          plan={plan}
          triggerText="Add copilot"
        />
      </Flex>
      <LimitExhaustedDialog
        isOpen={isLimitDialogOpen}
        onClose={() => setIsLimitDialogOpen(false)}
        isFreePlan={isFreePlan}
        usageType="agentic-interview"
      />
    </Stack>
  );
});

InterviewEmptyState.displayName = 'InterviewEmptyState';

export default InterviewEmptyState;
